import { useEffect, useState } from "react";
import { ShoppingBag, DollarSign, TrendingUp, Leaf } from "lucide-react";
import { fetchOrders } from "../../services/orderApi";

const AdminDashboard = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
  const loadOrders = async () => {
    try {
      const data = await fetchOrders();
      setOrders(data);
    } catch (err) {
      setError("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  loadOrders();
}, []);
  // Stats
  const totalOrders = orders.length;
  const totalRevenue = orders.reduce((sum, o) => sum + Number(o.totalAmount || 0), 0);
  const pendingOrders = orders.filter((o) => o.status === "pending").length;

  const dashboardStats = [
    {
      title: "Total Orders",
      value: totalOrders,
      icon: ShoppingBag,
      color: "bg-blue-500/10 text-blue-500",
    },
    {
      title: "Total Revenue",
      value: `$${totalRevenue.toFixed(2)}`,
      icon: DollarSign,
      color: "bg-amber-500/10 text-amber-500",
    },
    {
      title: "Pending Orders",
      value: pendingOrders,
      icon: TrendingUp,
      color: "bg-rose-500/10 text-rose-500",
    },
  ];

  const recentOrders = orders.slice(0, 5);

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-serif font-bold text-foreground mb-2">
          Welcome Back
        </h2>
        <p className="text-muted-foreground">
          Here's what's happening with your store today.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {dashboardStats.map((stat) => (
          <div key={stat.title} className="bg-card p-6 rounded-2xl shadow-soft">
            <div className="flex items-center justify-between mb-4">
              <div
                className={`w-12 h-12 rounded-xl flex items-center justify-center ${stat.color}`}
              >
                <stat.icon size={24} />
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-1">{stat.title}</p>
            <p className="text-2xl font-bold text-foreground">{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Recent Orders */}
      <div className="bg-card rounded-2xl shadow-soft overflow-hidden">
        <div className="p-6 border-b border-border">
          <h3 className="text-lg font-serif font-semibold text-foreground">
            Recent Orders
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-medium text-muted-foreground">
                  Order ID
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-muted-foreground">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-muted-foreground">
                  Items
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-muted-foreground">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-muted-foreground">
                  Total
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-border">

              {loading && (
                <tr>
                  <td colSpan="5" className="text-center py-6">
                    Loading Orders...
                  </td>
                </tr>
              )}

              {error && (
                <tr>
                  <td colSpan="5" className="text-center text-red-500 py-6">
                    {error}
                  </td>
                </tr>
              )}

              {!loading && !error && recentOrders.length === 0 && (
                <tr>
                  <td colSpan="5" className="text-center py-10">
                    <Leaf className="w-10 h-10 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">
                      No orders available at the moment.
                    </p>
                  </td>
                </tr>
              )}

              {!loading &&
                !error &&
                recentOrders.length > 0 &&
                recentOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-6 py-4 text-sm font-medium text-foreground">
                      {order.id}
                    </td>
                    <td className="px-6 py-4 text-sm text-foreground">
                      {order.customerName}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <ul className="space-y-1">
                        {order.items.map((item, i) => (
                          <li key={i}>
                            {item.productName} × {item.quantity}
                          </li>
                        ))}
                      </ul>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2.5 py-1 rounded-full text-xs bg-muted">
                        {order.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium">
                  ${(Number(order.totalAmount) || 0).toFixed(2)}
                    </td>
                  </tr>
                ))}

            </tbody>
          </table>

        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
